/**
 * Componente para gestión CRUD de categorías
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 5.2
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { ErpContext } from '../../../context/ErpContext';
import { SoporteContext } from '../../../context/SoporteContext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ModalCrearCategoria from '../modals/ModalCrearCategoria';

const GestionCategoriasComponent = () => {
  const { serverURL } = useContext(ErpContext);
  const { reloadCategorias } = useContext(SoporteContext);
  
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [categoriaEditar, setCategoriaEditar] = useState(null);
  
  useEffect(() => {
    cargarCategorias();
  }, []);
  
  const cargarCategorias = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${serverURL}/soporteAPI/categorias`, {
        withCredentials: true,
        params: { includeInactive: 'true' }
      });
      
      // El backend puede devolver { success: true, categorias: [...] } o directamente el array
      const categoriasData = response.data.categorias || response.data || [];
      setCategorias(categoriasData);
      
      // Recargar categorías en el contexto si existe la función
      if (reloadCategorias) {
        reloadCategorias();
      }
    } catch (err) {
      console.error('Error cargando categorías:', err);
      setError(err.response?.data?.error || 'Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCrear = () => {
    setCategoriaEditar(null);
    setModalOpen(true);
  };
  
  const handleEditar = (categoria) => {
    setCategoriaEditar(categoria);
    setModalOpen(true);
  };
  
  const handleEliminar = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta categoría?')) return;
    
    try {
      await axios.delete(`${serverURL}/soporteAPI/categorias/${id}`, {
        withCredentials: true
      });
      setSuccess('Categoría eliminada correctamente');
      cargarCategorias();
    } catch (err) {
      console.error('Error eliminando categoría:', err);
      setError(err.response?.data?.error || 'Error al eliminar la categoría');
    }
  };
  
  const handleModalClose = (reload) => {
    setModalOpen(false);
    setCategoriaEditar(null);
    if (reload) {
      cargarCategorias();
      setSuccess('Categoría guardada correctamente');
    }
  };
  
  const columns = [
    { 
      field: 'nombre', 
      headerName: 'Nombre', 
      flex: 1,
      minWidth: 150
    },
    { 
      field: 'modulo', 
      headerName: 'Módulo', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value || 'N/A'} 
          size="small" 
          variant="outlined"
        />
      )
    },
    { 
      field: 'descripcion', 
      headerName: 'Descripción', 
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || 'Sin descripción'}
        </Typography>
      )
    },
    {
      field: 'orden',
      headerName: 'Orden',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 0}
        </Typography>
      )
    },
    {
      field: 'activa',
      headerName: 'Activa',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Sí' : 'No'}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      )
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton 
              size="small" 
              onClick={() => handleEditar(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton 
              size="small" 
              color="error" 
              onClick={() => handleEliminar(params.row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];
  
  if (loading && categorias.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Gestión de Categorías</Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Actualizar">
            <IconButton onClick={cargarCategorias} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCrear}
          >
            Nueva Categoría
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={categorias}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 }
            }
          }}
          disableRowSelectionOnClick
          loading={loading}
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Box>
      
      <ModalCrearCategoria
        open={modalOpen}
        onClose={handleModalClose}
        categoria={categoriaEditar}
      />
    </Paper>
  );
};

export default GestionCategoriasComponent;

